import React, { useEffect, useState } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { MODEL_OPTIONS } from '../../constants/modelOptions';
import apiService from '../../services/api';

interface TestPromptModalProps {
    isOpen: boolean;
    onClose: () => void;
    meetingId: number;
    meetingTypeId: number;
    token: string;
    userEmail: string;
}

export const TestPromptModal: React.FC<TestPromptModalProps> = ({
    isOpen,
    onClose,
    meetingId,
    meetingTypeId,
    token,
    userEmail,
}) => {
    const [promptFiles, setPromptFiles] = useState<string[]>([]);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState<string>('');
    const [selectedProvider, setSelectedProvider] = useState<string>('');
    const [selectedModel, setSelectedModel] = useState<string>('');
    const [testType, setTestType] = useState<'summary' | 'agenda'>('summary');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isStarted, setIsStarted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setIsLoadingFiles(true);
        setError(null);
        apiService.getPromptFiles(token)
            .then(files => setPromptFiles(files))
            .catch((err: any) => {
                console.error('Failed to load prompt files:', err);
                setError(err.response?.data?.detail || err.message || 'Failed to load prompt files');
            })
            .finally(() => setIsLoadingFiles(false));
    }, [isOpen, token]);

    const handleRun = async () => {
        if (!selectedPrompt || !selectedModel) {
            setError('Please select a prompt file and a model');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await apiService.runTestPrompt({
                token,
                meeting_id: meetingId,
                prompt_name: selectedPrompt,
                model_name: selectedModel,
                test_type: testType,
            });
            setIsStarted(true);
        } catch (err: any) {
            console.error('Failed to run test prompt:', err);
            setError(err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to run test prompt');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setSelectedPrompt('');
            setSelectedProvider('');
            setSelectedModel('');
            setTestType('summary');
            setError(null);
            setIsStarted(false);
            onClose();
        }
    };

    return (
        <ModalWrapper modalId="TestPromptModal"
            isOpen={isOpen}
            onClose={handleClose}
            title="Test Prompt"
            isLoading={isSubmitting}
            width="max-w-lg"
        >
            {isStarted ? (
                <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                        Test prompt started. The prompt and the model result will be sent to {userEmail}.
                    </div>
                    <div className="flex gap-3 justify-end pt-2">
                        <button onClick={handleClose} className={POPUP_STYLES.button}>
                            Close
                        </button>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Summary / Agenda selection (CM intake meetings only) */}
                    {meetingTypeId === 1 && (
                        <div>
                            <label className="block text-sm font-medium text-[#366A75] mb-2">
                                Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={testType}
                                onChange={(e) => { setTestType(e.target.value as 'summary' | 'agenda'); setError(null); }}
                                className={POPUP_STYLES.input}
                                disabled={isSubmitting}
                            >
                                <option value="summary">Summary</option>
                                <option value="agenda">Agenda</option>
                            </select>
                        </div>
                    )}

                    {/* Prompt file selection */}
                    <div>
                        <label className="block text-sm font-medium text-[#366A75] mb-2">
                            Prompt <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedPrompt}
                            onChange={(e) => { setSelectedPrompt(e.target.value); setError(null); }}
                            className={POPUP_STYLES.input}
                            disabled={isSubmitting || isLoadingFiles}
                        >
                            <option value="">
                                {isLoadingFiles ? 'Loading prompt files...' : '-- Select a prompt file --'}
                            </option>
                            {promptFiles.map((file) => (
                                <option key={file} value={file}>
                                    {file}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Model provider selection */}
                    <div>
                        <label className="block text-sm font-medium text-[#366A75] mb-2">
                            Model Provider <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedProvider}
                            onChange={(e) => { setSelectedProvider(e.target.value); setSelectedModel(''); setError(null); }}
                            className={POPUP_STYLES.input}
                            disabled={isSubmitting}
                        >
                            <option value="">-- Select a provider --</option>
                            {Object.keys(MODEL_OPTIONS).map((provider) => (
                                <option key={provider} value={provider}>
                                    {provider}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Model selection */}
                    <div>
                        <label className="block text-sm font-medium text-[#366A75] mb-2">
                            Model <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={selectedModel}
                            onChange={(e) => { setSelectedModel(e.target.value); setError(null); }}
                            className={POPUP_STYLES.input}
                            disabled={isSubmitting || !selectedProvider}
                        >
                            <option value="">
                                {selectedProvider ? '-- Select a model --' : '-- Select a provider first --'}
                            </option>
                            {(MODEL_OPTIONS[selectedProvider] || []).map((model) => (
                                <option key={model} value={model}>
                                    {model}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="text-sm text-[#366A75]">
                        The summary will not be saved on the meeting - the result will be emailed to {userEmail}.
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 justify-end pt-2">
                        <button
                            onClick={handleClose}
                            className={POPUP_STYLES.button}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRun}
                            className={POPUP_STYLES.button}
                            disabled={!selectedPrompt || !selectedModel || isSubmitting}
                        >
                            {isSubmitting ? 'Starting...' : 'Run'}
                        </button>
                    </div>
                </div>
            )}
        </ModalWrapper>
    );
};
